import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { IAccount } from "@/models/account/account.types"
import { AccountService } from "@/services/AccountService"
import { AccountSearchTerm } from "@/stores/customerSearch"
import mpxnValidator from "@/validators/mpxn"
import { DEFAULT_SEARCH_LIMIT } from "@/constants/customerSearch"

export interface SearchAccountsResult {
  accounts: IAccount[]
  limitReached: boolean
}

/**
 * Checks if a search result has hit the limit
 */
const hasHitLimit = (results: unknown[]): boolean => {
  return results.length >= DEFAULT_SEARCH_LIMIT
}

/**
 * Service handler for searching accounts using Hasura queries
 * Combines results from multiple search queries and fetches full account details
 */
export const searchAccounts = (
  searchTerm: AccountSearchTerm,
  searchQuery: string,
) => {
  const queries = new QueryAggregator()

  let accountNumbers: string[] = []
  let limitReached = false

  switch (searchTerm) {
    case "email":
    case "telephone": {
      const contactQuery =
        AccountService.searchAccountsByPhoneOrEmail(searchQuery)
      queries.add(contactQuery)
      accountNumbers = contactQuery.data
      limitReached = hasHitLimit(accountNumbers)
      break
    }

    case "surname": {
      const customerQuery = AccountService.searchAccountsBySurname(searchQuery)
      queries.add(customerQuery)
      accountNumbers = customerQuery.data
      limitReached = hasHitLimit(accountNumbers)
      break
    }

    case "postcode": {
      const contactQuery = AccountService.searchAccountsByPostcode(searchQuery)
      queries.add(contactQuery)
      accountNumbers = contactQuery.data
      limitReached = hasHitLimit(accountNumbers)
      break
    }

    case "mpxn": {
      // Validate MPXN format before querying
      const validationResult = mpxnValidator(searchQuery)
      if (validationResult !== true) break

      const meterPointQuery =
        AccountService.searchMeterPointsByIdentifier(searchQuery)
      queries.add(meterPointQuery)

      const meterPointIds = meterPointQuery.data
      if (meterPointIds.length === 0) break
      if (hasHitLimit(meterPointIds)) {
        limitReached = true
      }

      const billingAccountQuery =
        AccountService.searchAccountsByMeterPointIds(meterPointIds)
      queries.add(billingAccountQuery)

      const billingAccountIds = billingAccountQuery.data
      if (billingAccountIds.length === 0) break

      const accountNumbersQuery =
        AccountService.getAccountNumbersByCoreIds(billingAccountIds)
      queries.add(accountNumbersQuery)

      accountNumbers = accountNumbersQuery.data
      if (hasHitLimit(accountNumbers)) {
        limitReached = true
      }
      break
    }

    default:
      break
  }

  const accounts: IAccount[] = []
  for (const accountNumber of accountNumbers) {
    const accountQuery = AccountService.getAccount(accountNumber)
    queries.add(accountQuery)
    if (accountQuery.data) {
      accounts.push(accountQuery.data)
    }
  }

  const result: SearchAccountsResult = {
    accounts,
    limitReached,
  }

  return queries.setData(result).getAggregate()
}
